import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { deleteUserFromGroups } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { User } from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../utils/use-user-settings';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
interface DeleteUserFromGroupDialogProps {
  user: User;
  open: boolean;
  handleClose: () => void;
  selectedRows: string[];
  userEmail: string;
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
}

export default function DeleteUserFromGroupDialog({
  open,
  handleClose,
  setSelectedRows,
  selectedRows,
  userEmail,
  user,
}: DeleteUserFromGroupDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();
  const { changeLogRoles } = useUserSettings();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteUserFromGroups(userEmail, selectedRows, 'Group', changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message:
          'The task to remove this user from the selected group(s) has started.',
      });

      setSelectedRows([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue removing the user from the selected group(s). Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_details.groups.delete_groups, {
        module: ModuleList.modules.userManagement.name,
        section: ModuleList.modules.userManagement.sections.groups,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, UserRoles.RemoveGroupMembers.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Remove from Group"
      dialogContent={
        <Box>
          <Typography sx={{ mb: 2 }}>
            {selectedRows.length > 1 ? (
              <>
                Remove <strong>{user.primaryEmail}</strong> from{' '}
                <strong>{selectedRows.length}</strong> groups.
              </>
            ) : (
              <>
                Remove <strong>{user.primaryEmail}</strong> from{' '}
                <strong>{selectedRows[0]}</strong>.
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Remove',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'warning',
      }}
    />
  );
}
